import { IList } from 'annotation-tools/utils';
import React, { memo } from 'react';
import { map } from 'rxjs/operators';
import { defaultCropRectRelative } from '../constants/default-crop-rect';
import {
  AnnotationCanvas,
  AnnotationCanvasStylePartialType,
  defaultId,
  IBbox,
  IBboxType
} from '../imports/annotation-canvas';
import {
  filterNotUndefined,
  IImageType,
  ImgWithLoadingState,
  IRectType,
  mergeTyped,
  useDataStream,
  useStateAsStream,
  useStreamEffect,
  useStreamValue,
  useValueAsStream
} from '../imports/utils';

const initialBbox = IBbox({
  id: defaultId,
  rect: defaultCropRectRelative
});

export const ImageCropper = memo(
  ({
    styles,
    fullImageData,
    cropRectRelative,
    onCropRectRelativeChange
  }: Readonly<{
    styles: AnnotationCanvasStylePartialType;
    fullImageData: IImageType;
    cropRectRelative: IRectType;
    onCropRectRelativeChange: (v: IRectType) => void;
  }>) => {
    const cropRectInput$ = useValueAsStream<IRectType>(cropRectRelative);

    const [
      bboxListRelativeFromAnnotationTool$,
      bboxListRelativeFromAnnotationToolOnChange
    ] = useStateAsStream<IList<IBboxType>>(IList<IBboxType>());

    const bboxRelativeFromAnnotationTool$ = useDataStream<
      IBboxType | undefined
    >(
      undefined,
      bboxListRelativeFromAnnotationTool$.pipe(map(list => list.last()))
    );

    const bboxRelativeFromProps$ = useDataStream<IBboxType | undefined>(
      undefined,
      cropRectInput$.pipe(map(rect => IBbox({ id: defaultId, rect })))
    );

    // bboxは1つのみ持つ
    const bboxRelative$ = useDataStream<IBboxType>(
      initialBbox,
      mergeTyped(bboxRelativeFromAnnotationTool$, bboxRelativeFromProps$).pipe(
        filterNotUndefined()
      )
    );

    const bboxListRelative$ = useDataStream<IList<IBboxType>>(
      IList<IBboxType>(),
      bboxRelative$.pipe(
        map(bb => IList<IBboxType>([bb]))
      )
    );

    /* extract values */
    const bboxListRelative = useStreamValue(
      bboxListRelative$,
      IList<IBboxType>()
    );

    /* effects */
    useStreamEffect(bboxRelativeFromAnnotationTool$, bbox => {
      if (bbox !== undefined) {
        onCropRectRelativeChange(bbox.rect);
      }
    });

    return (
      <AnnotationCanvas
        annotationMode='rectbox'
        imagePixelSize={fullImageData.rectSize}
        styles={styles}
        zoomable={false}
        bboxesRelative={bboxListRelative}
        onBboxesRelativeChange={bboxListRelativeFromAnnotationToolOnChange}
      >
        <ImgWithLoadingState fullImgSrc={fullImageData.src} />
      </AnnotationCanvas>
    );
  }
);

ImageCropper.displayName = 'ImageCropper';
